import type { TimeFilterValue } from '~/pages/Dashboard/dashboardSelectors';

import type {
  Product,
  ProductAnalytics,
  ProductDashboardItem,
  ProductProjectStatusCounts,
  ProjectBoardSort,
  ProjectPage,
  ProjectStatusGroup,
  ProjectWithTasks,
  Repository,
} from '~/types';

import { apiRequest } from './client';
import { ENDPOINTS, HTTP_METHODS } from './config';

export async function addProductRepository(productId: string, repositoryId: string): Promise<void> {
  await apiRequest(`${ENDPOINTS.PRODUCTS}/${productId}/repositories`, {
    method: HTTP_METHODS.POST,
    body: JSON.stringify({ repository_id: repositoryId }),
  });
}

export async function createProduct(data: {
  description?: string;
  name: string;
}): Promise<Product> {
  const response = await apiRequest<Product>(ENDPOINTS.PRODUCTS, {
    method: HTTP_METHODS.POST,
    body: JSON.stringify(data),
  });
  return response.data;
}

export async function deleteProduct(id: string): Promise<void> {
  await apiRequest(`${ENDPOINTS.PRODUCTS}/${id}`, {
    method: HTTP_METHODS.DELETE,
  });
}

export async function getProduct(id: string): Promise<Product> {
  const response = await apiRequest<Product>(`${ENDPOINTS.PRODUCTS}/${id}`);
  return response.data;
}

export async function getProductAnalytics(productId: string): Promise<ProductAnalytics> {
  const response = await apiRequest<ProductAnalytics>(
    `${ENDPOINTS.PRODUCTS}/${productId}/analytics`,
  );
  return response.data;
}

/**
 * Fetch the batch dashboard payload, scoped server-side to each section's
 * independent time window.
 *
 * Each caller (`useDashboardInProgress`/`useDashboardCompleted`) sends only
 * the window it owns — the other param is omitted, not defaulted here, so
 * the two sections' queries stay independently keyed and neither refetches
 * when the other's window changes. The server defaults an omitted window to
 * `week` (see `DEFAULT_DASHBOARD_TIME_WINDOW` in `apps/api/src/constants.py`).
 */
export async function getProductDashboard(params: {
  completedWindow?: TimeFilterValue;
  inProgressWindow?: TimeFilterValue;
}): Promise<ProductDashboardItem[]> {
  const queryParams = new URLSearchParams();
  if (params.inProgressWindow) {
    queryParams.set('in_progress_window', params.inProgressWindow);
  }
  if (params.completedWindow) {
    queryParams.set('completed_window', params.completedWindow);
  }
  const response = await apiRequest<ProductDashboardItem[]>(
    `${ENDPOINTS.PRODUCTS}/dashboard?${queryParams.toString()}`,
  );
  return response.data;
}

export async function getProductProjects(productId: string): Promise<ProjectWithTasks[]> {
  const response = await apiRequest<ProjectWithTasks[]>(
    `${ENDPOINTS.PRODUCTS}/${productId}/projects`,
  );
  return response.data;
}

/**
 * Fetch one cursor-paginated page of a product board column's projects.
 *
 * `cursor` is the opaque `nextCursor` from a previous page (omit/pass `null`
 * for the first page); the backend rejects a cursor issued for a different
 * productId/statusGroup/sort combination with a 400.
 */
export async function getProductProjectsPage(
  productId: string,
  params: {
    cursor: string | null;
    limit: number;
    q: string | undefined;
    sort: ProjectBoardSort;
    statusGroup: ProjectStatusGroup;
  },
): Promise<ProjectPage> {
  const queryParams = new URLSearchParams({
    status_group: params.statusGroup,
    sort: params.sort,
    limit: String(params.limit),
  });
  if (params.cursor) {
    queryParams.set('cursor', params.cursor);
  }
  if (params.q) {
    queryParams.set('q', params.q);
  }

  const response = await apiRequest<ProjectPage>(
    `${ENDPOINTS.PRODUCTS}/${productId}/projects?${queryParams.toString()}`,
  );
  return response.data;
}

/**
 * Fetch a product board's per-column project counts, independent of
 * pagination (used for the column header badges and search-scoped totals).
 */
export async function getProductProjectStatusCounts(
  productId: string,
  params: { q: string | undefined },
): Promise<ProductProjectStatusCounts> {
  const queryParams = new URLSearchParams();
  if (params.q) {
    queryParams.set('q', params.q);
  }
  const query = queryParams.toString();

  const response = await apiRequest<ProductProjectStatusCounts>(
    `${ENDPOINTS.PRODUCTS}/${productId}/projects/counts${query ? `?${query}` : ''}`,
  );
  return response.data;
}

export async function getProductRepositories(productId: string): Promise<Repository[]> {
  const response = await apiRequest<Repository[]>(
    `${ENDPOINTS.PRODUCTS}/${productId}/repositories`,
  );
  return response.data;
}

export async function getProducts(): Promise<Product[]> {
  const response = await apiRequest<Product[]>(ENDPOINTS.PRODUCTS);
  return response.data;
}

export async function linkProductProject(productId: string, projectId: string): Promise<void> {
  await apiRequest(`${ENDPOINTS.PRODUCTS}/${productId}/projects`, {
    method: HTTP_METHODS.POST,
    body: JSON.stringify({ project_id: projectId }),
  });
}

export async function removeProductRepository(
  productId: string,
  repositoryId: string,
): Promise<void> {
  await apiRequest(`${ENDPOINTS.PRODUCTS}/${productId}/repositories/${repositoryId}`, {
    method: HTTP_METHODS.DELETE,
  });
}

export async function unlinkProductProject(productId: string, projectId: string): Promise<void> {
  await apiRequest(`${ENDPOINTS.PRODUCTS}/${productId}/projects/${projectId}`, {
    method: HTTP_METHODS.DELETE,
  });
}

export async function updateProduct(
  id: string,
  data: { description?: string; name?: string; slug?: string },
): Promise<Product> {
  const response = await apiRequest<Product>(`${ENDPOINTS.PRODUCTS}/${id}`, {
    method: HTTP_METHODS.PUT,
    body: JSON.stringify(data),
  });
  return response.data;
}
